import { useTheme } from "@/theme/ThemeContext";
import { useEffect, useRef, useMemo, useCallback } from "react";
import Background3D from "./Backgrounds/AboutBackground";
import { motion, useAnimation, useScroll, useTransform } from "framer-motion";
import { theme } from "@/theme/theme";

// Constants moved outside component to prevent recreation
const SKILLS = [
  "React", "Vue", "Next.js", "Nuxt.js", "TypeScript", "Tailwind CSS", 
  "Bootstrap", "Material-UI", "Vuetify", "React Router", "Vuex", "Pinia", 
  "Redux", "Redux Toolkit", "Context API", "Node.js", "MongoDB", "Git", 
  "REST APIs", "Figma"
];

const EXPERIENCE_START_DATE = new Date('2023-02-01');

const About = () => {
  const { theme: themeMode } = useTheme();
  const isDark = themeMode === "dark";
  const aboutStyles = theme.about[themeMode];

  // Memoize experience calculation
  const experienceYears = useMemo(() => {
    const currentDate = new Date();
    const diffTime = currentDate.getTime() - EXPERIENCE_START_DATE.getTime();
    const diffYears = diffTime / (1000 * 60 * 60 * 24 * 365.25);
    return Math.max(0, diffYears).toFixed(1);
  }, []);

  // Scroll-based scaling with improved viewport tracking
  const cardRef = useRef<HTMLDivElement>(null);
  const controls = useAnimation();
  const { scrollYProgress } = useScroll({
    target: cardRef,
    offset: ["start end", "end start"]
  });

  // Transform scroll progress to scale
  const scale = useTransform(scrollYProgress, [0, 0.5, 1], [0.7, 1, 0.7]);

  // Memoize animation values
  const animationValues = useMemo(() => ({
    dark: {
      rotateY: 15,
      boxShadow: "0 8px 60px 0 rgba(249,115,22,0.45), 0 0 0 2px rgba(255,255,255,0.15)"
    },
    light: {
      rotateY: 15,
      boxShadow: "0 8px 60px 0 rgba(20,184,166,0.25), 0 0 0 2px rgba(0,0,0,0.08)"
    }
  }), []);

  // Optimize theme change animation with useCallback
  const handleThemeAnimation = useCallback(async () => {
    // Pulse the 3D rays background
    window.dispatchEvent?.(new CustomEvent('background3d-burst'));
    
    // Animate card shadow/glow pulse with subtle flip
    const initialAnimation = isDark ? animationValues.dark : animationValues.light;
    
    await controls.start({
      ...initialAnimation,
      transition: { duration: 0.4, ease: [0.4, 0, 0.2, 1] }
    });
    
    controls.start({
      rotateY: 0,
      boxShadow: aboutStyles.card.shadow,
      transition: { duration: 0.6, ease: [0.4, 0, 0.2, 1] }
    });
  }, [isDark, controls, aboutStyles.card.shadow, animationValues]);

  // Animate on theme change
  useEffect(() => {
    handleThemeAnimation();
  }, [handleThemeAnimation]);

  return (
    <section id="about" className={`relative min-h-screen flex items-center justify-center ${aboutStyles.background} overflow-hidden py-16 sm:py-24 px-4 sm:px-8 font-mono backdrop-blur-sm`}>
      {/* 3D Background */}
      <Background3D />

      <div className="w-full max-w-7xl mx-auto relative z-10 flex flex-col items-center justify-center min-h-[80vh]">
        <div className="w-full max-w-3xl flex flex-col items-center justify-center">

          <motion.div
            ref={cardRef}
            style={{
              scale,
              perspective: "1000px",
              transformStyle: "preserve-3d",
              transition: "transform 0.8s cubic-bezier(.4,2,.6,1), box-shadow 0.5s cubic-bezier(.4,2,.6,1), background 0.5s, color 0.5s",
              willChange: "transform, box-shadow, background, color",
            }}
            animate={{
              opacity: 1,
              boxShadow: aboutStyles.card.shadow,
            }}
            initial={{ opacity: 0, scale: 0.8 }}
            transition={{
              duration: 0.8,
              ease: [0.4, 0, 0.2, 1],
              opacity: { duration: 0.6, delay: 0.2 },
            }}
            className={`${aboutStyles.card.background} rounded-2xl sm:rounded-3xl px-6 sm:px-8 py-6 sm:py-8 ring-2 ${aboutStyles.ring.accent} flex flex-col items-center backdrop-blur-xl relative font-mono scale-animation`}
          >
            {/* Decorative Elements */}
            <div className="absolute inset-0 rounded-2xl sm:rounded-3xl overflow-hidden">
              <div className={`absolute inset-0 bg-gradient-to-br ${aboutStyles.card.gradient}`} />
              <div className={`absolute inset-0 bg-[radial-gradient(circle_at_50%_120%,${aboutStyles.card.radialGradient},transparent_70%)]`} />
            </div>

            {/* About Me Content */}
            <div className="text-center w-full">
              <h2 className={`text-2xl sm:text-3xl font-extrabold ${aboutStyles.text.accent} mb-3 sm:mb-4 font-mono transition-colors duration-500`}>About Me</h2>
              <div className="w-12 sm:w-16 h-1 rounded-full mb-4 sm:mb-6 bg-gradient-to-r from-orange-400/60 to-teal-400/60 mx-auto transition-colors duration-500" />
              <div className="space-y-3 sm:space-y-4 text-left">
                <p className={`text-sm sm:text-base leading-relaxed ${aboutStyles.text.main} font-light font-mono transition-colors duration-500`}>
                  I'm a <span className={`font-semibold ${aboutStyles.text.accent}`}>Frontend-Focused Full Stack Developer</span> with deep expertise in 
                  creating stunning, high-performance user interfaces and seamless digital experiences.
                </p>
                <p className={`text-sm sm:text-base leading-relaxed ${aboutStyles.text.main} font-light font-mono transition-colors duration-500`}>
                  My core strength lies in <span className={`font-semibold ${aboutStyles.text.accent}`}>advanced frontend development</span> using 
                  <span className="font-semibold"> React.js</span>, <span className="font-semibold">Vue.js</span>, and <span className="font-semibold">TypeScript</span>, 
                  crafting pixel-perfect applications. I complement this with solid <span className={`font-semibold ${aboutStyles.text.accent}`}>backend knowledge</span> in 
                  <span className="font-semibold"> Node.js</span> and <span className="font-semibold">MongoDB</span>.
                </p>
                <p className={`text-sm sm:text-base leading-relaxed ${aboutStyles.text.main} font-light font-mono transition-colors duration-500`}>
                  As a <span className={`font-semibold ${aboutStyles.text.accent}`}>frontend architecture enthusiast</span>, 
                  I create intuitive, accessible interfaces that users love while staying current with modern development practices.
                </p>
              </div>
            </div>

            {/* Skills Section */}
            <div className="flex flex-wrap gap-2 sm:gap-3 justify-center mt-6 sm:mt-8">
              {SKILLS.map((skill) => (
                <span
                  key={skill}
                  className={`px-3 sm:px-4 py-1.5 sm:py-2 rounded-full text-xs sm:text-sm font-medium border transform-gpu transition-all duration-300 hover:scale-110 hover:-translate-y-1 font-mono
                    ${aboutStyles.skills.border} ${aboutStyles.skills.text} ${aboutStyles.skills.hover.background} ${aboutStyles.skills.hover.shadow}`}
                >
                  {skill}
                </span>
              ))}
            </div>

            {/* Info Cards */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 mt-5 sm:mt-6 w-full">
              {/* Education */}
              <div className="flex items-center gap-3 sm:gap-4 p-3 sm:p-4 rounded-xl border border-transparent hover:border-orange-400/40 dark:hover:border-orange-400/40 transition-all duration-300 bg-transparent hover:scale-105 hover:-translate-y-1">
                <div className={`w-8 h-8 sm:w-10 sm:h-10 rounded-full flex items-center justify-center ${aboutStyles.info.icon.background}`}>
                  <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4.26 10.147a60.436 60.436 0 00-.491 6.347A48.627 48.627 0 0112 20.904a48.627 48.627 0 018.232-4.41 60.46 60.46 0 00-.491-6.347m-15.482 0a50.57 50.57 0 00-2.658-.813A59.905 59.905 0 0112 3.493a59.902 59.902 0 0110.399 5.84c-.896.248-1.783.52-2.658.814m-15.482 0A50.697 50.697 0 0112 13.489a50.702 50.702 0 017.74-3.342M6.75 15a.75.75 0 100-1.5.75.75 0 000 1.5zm0 0v-3.675A55.378 55.378 0 0112 8.443m-7.007 11.55A5.981 5.981 0 006.75 15.75v-1.5" />
                  </svg>
                </div>
                <div>
                  <h4 className={`text-xs sm:text-sm font-semibold ${aboutStyles.text.accent} font-mono transition-colors duration-500`}>Education</h4>
                  <p className="text-xs sm:text-sm font-medium text-neutral-700 dark:text-neutral-200 font-mono">B.Tech Computer Engineering</p>
                  <p className={`text-xs sm:text-sm ${aboutStyles.info.text.accent} font-mono transition-colors duration-500`}>Govt. Engineering College, Dahod</p>
                </div>
              </div>

              {/* Experience */}
              <div className="flex items-center gap-3 sm:gap-4 p-3 sm:p-4 rounded-xl border border-transparent hover:border-orange-400/40 dark:hover:border-orange-400/40 transition-all duration-300 bg-transparent hover:scale-105 hover:-translate-y-1">
                <div className={`w-8 h-8 sm:w-10 sm:h-10 rounded-full flex items-center justify-center ${aboutStyles.info.icon.background}`}>
                  <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17.25 6.75L22.5 12l-5.25 5.25m-10.5 0L1.5 12l5.25-5.25m7.5-3l-4.5 16.5" />
                  </svg>
                </div>
                <div>
                  <h4 className={`text-xs sm:text-sm font-semibold ${aboutStyles.text.accent} font-mono transition-colors duration-500`}>Experience</h4>
                  <p className="text-xs sm:text-sm font-medium text-neutral-700 dark:text-neutral-200 font-mono">Full Stack Developer</p>
                  <p className={`text-xs sm:text-sm ${aboutStyles.info.text.accent} font-mono transition-colors duration-500`}>{experienceYears} Years in Web Development</p>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default About;